import { useMemo, useState } from "react";
import {
  NLSFB,
  nlsfb,
  nlsfbChildren,
  nlsfbLabel,
  nlsfbNode,
  nlsfbPath,
  nlsfbSearch,
  type NlsfbNode,
} from "@klantpunt/shared";
import { AssetBadge } from "../assets/assetIcons";

/** Ouder in de boom: "56.24" → "56.2", "56.2" → "56", "56" → null. */
function parentOf(code: string): string | null {
  const path = nlsfbPath(code);
  return path.length > 1 ? path[path.length - 2]!.code : null;
}

/**
 * Element kiezen volgens NLBE-SfB tabel 1 (docs/NLSFB.md): zoeken op code en woorden, of via de boom
 * hoofdgroep → element → subelement → detail. De diepte is vrij. Zonder de volledige tabel (`full`
 * uit) alleen elementen met 2 cijfers.
 */
export function ElementPicker({
  value,
  onChange,
  full,
  suggestions = [],
  recent = [],
  defaultOpen = false,
  emptyLabel = "Niet ingedeeld",
}: {
  value: string | null;
  onChange: (code: string | null) => void;
  full: boolean;
  /** Voorstellen (bv. van de asset, trefwoorden of het type), eerste is het beste. */
  suggestions?: string[];
  /** Laatst gebruikte codes (per gebouw, op dit apparaat). */
  recent?: string[];
  defaultOpen?: boolean;
  emptyLabel?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  /** Code waarvan nu de onderliggende codes getoond worden; null = alle hoofdgroepen. */
  const [drill, setDrill] = useState<string | null>(null);
  const hits = useMemo(() => nlsfbSearch(q, { full, limit: 40 }), [q, full]);
  const cur = nlsfb(value);
  const deeper = (code: string) => full && nlsfbChildren(code).length > 0;
  const close = () => {
    setOpen(false);
    setQ("");
    setDrill(null);
  };
  const pick = (code: string | null) => {
    onChange(code);
    close();
  };
  const openPanel = () => {
    setOpen(true);
    // Verder waar de huidige keuze zit.
    setDrill(value && full ? (deeper(value) ? value : parentOf(value)) : null);
  };
  const tile = (n: NlsfbNode, drillable = deeper(n.code)) => (
    <button
      key={n.code}
      type="button"
      className="cattile"
      aria-current={n.code === value || undefined}
      onClick={() => (drillable ? (setDrill(n.code), setQ("")) : pick(n.code))}
    >
      <AssetBadge code={n.code} size={30} />
      <span className="grow">
        <b className="num">{n.code}</b> {n.label}
        {drillable && <span className="muted small block">{nlsfbChildren(n.code).length} onderdelen ›</span>}
      </span>
    </button>
  );
  const visibleSuggestions = suggestions.filter((c) => c !== value && nlsfbNode(c)).slice(0, 3);
  const visibleRecent = recent
    .filter((c) => c !== value && nlsfbNode(c) && !visibleSuggestions.includes(c))
    .slice(0, 3);
  const chips = (label: string, codes: string[]) =>
    codes.length > 0 && (
      <div className="catsuggest" role="group" aria-label={label}>
        <span className="muted small">{label}:</span>
        {codes.map((c) => (
          <button key={c} type="button" className="chip chipbtn" onClick={() => pick(c)}>
            <AssetBadge code={c} size={18} />
            {nlsfbLabel(c)}
          </button>
        ))}
      </div>
    );
  const node = drill ? nlsfbNode(drill) : undefined;

  return (
    <div className="catpicker">
      <div className="catcur">
        <AssetBadge code={value} size={36} />
        <span className="grow">
          <strong>{value ? nlsfbLabel(value) : emptyLabel}</strong>
          {cur.main && (
            <span className="muted small block">
              {cur.main.code}- {cur.main.label}
            </span>
          )}
        </span>
        {value && !open && (
          <button type="button" className="btn small" onClick={() => onChange(null)}>
            Wissen
          </button>
        )}
        <button type="button" className="btn small" aria-expanded={open} onClick={() => (open ? close() : openPanel())}>
          {open ? "Inklappen" : value ? "Wijzigen" : "Kiezen"}
        </button>
      </div>
      {chips("Laatst gebruikt", visibleRecent)}
      {chips("Voorstel", visibleSuggestions)}
      {open && (
        <div className="catlist">
          <input
            type="search"
            aria-label="Element zoeken"
            placeholder={
              full ? "Zoek op code of woord, bv. 56.2 of ketel" : "Zoek op nummer of naam, bv. 56 of verwarming"
            }
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q.trim() ? (
            <div role="group" aria-label="Zoekresultaten">
              <div className="cattiles">{hits.map((n) => tile(n, false))}</div>
              {hits.length === 0 && <p className="muted">Geen element gevonden.</p>}
            </div>
          ) : node ? (
            <div role="group" aria-label={`Onderdelen van ${node.code} ${node.label}`}>
              <div className="row">
                <button type="button" className="btn small" onClick={() => setDrill(parentOf(node.code))}>
                  ‹ Terug
                </button>
                <span className="muted small grow">
                  {nlsfbPath(node.code)
                    .map((p) => p.code)
                    .join(" › ")}
                </span>
              </div>
              <div className="cattiles">
                <button
                  type="button"
                  className="cattile"
                  aria-current={value === node.code || undefined}
                  onClick={() => pick(node.code)}
                >
                  <AssetBadge code={node.code} size={30} />
                  <span className="grow">
                    Alleen <b className="num">{node.code}</b> {node.label}
                    <span className="muted small block">zonder verdere indeling</span>
                  </span>
                </button>
                {nlsfbChildren(node.code).map((n) => tile(n))}
              </div>
            </div>
          ) : (
            <div role="group" aria-label="Elementen per hoofdgroep">
              {NLSFB.map((m) => (
                <fieldset key={m.code} className="catgroup">
                  <legend className="label">
                    {m.code}- {m.label}
                  </legend>
                  <div className="cattiles">
                    {m.groups.map((g) => tile({ code: g.code, label: g.label, level: 2 }))}
                  </div>
                </fieldset>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
